/**
 * VaultSecurityContext.js
 * 
 * Sikkerhetskontekst for Vault (dokumenthvelv)
 * Håndterer:
 * - 30 min inaktivitetstimeout
 * - Re-autentisering ved timeout
 * - Vault session tokens
 * - Aktivitetssporing
 * 
 * @version 1.0.0
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Storage key for vault token
const VAULT_TOKEN_KEY = 'jm_vault_token';
const VAULT_EXPIRES_KEY = 'jm_vault_expires';

// Inaktivitetstimeout i millisekunder (30 minutter)
const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000;

// Aktivitetsevents å lytte på
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll'];

const VaultSecurityContext = createContext(null);

export function VaultSecurityProvider({ children }) {
  const { token, user } = useAuth();
  
  // Vault session state
  const [vaultToken, setVaultToken] = useState(() => sessionStorage.getItem(VAULT_TOKEN_KEY));
  const [vaultExpires, setVaultExpires] = useState(() => {
    const stored = sessionStorage.getItem(VAULT_EXPIRES_KEY);
    return stored ? new Date(stored) : null;
  });
  const [isVaultLocked, setIsVaultLocked] = useState(true);
  const [remainingTime, setRemainingTime] = useState(0);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  
  // Activity tracking
  const lastActivityRef = useRef(Date.now());
  const inactivityTimerRef = useRef(null);
  const countdownTimerRef = useRef(null);
  
  // Check if vault session is valid
  const checkVaultSession = useCallback(() => {
    if (!vaultToken || !vaultExpires) {
      setIsVaultLocked(true);
      setRemainingTime(0);
      return false;
    }
    
    const now = new Date();
    const expiresDate = new Date(vaultExpires);
    
    if (now >= expiresDate) {
      // Session expired
      sessionStorage.removeItem(VAULT_TOKEN_KEY);
      sessionStorage.removeItem(VAULT_EXPIRES_KEY);
      setVaultToken(null);
      setVaultExpires(null);
      setIsVaultLocked(true);
      setRemainingTime(0);
      return false;
    }
    
    // Calculate remaining time
    const remaining = Math.floor((expiresDate - now) / 1000);
    setRemainingTime(remaining);
    setIsVaultLocked(false);
    return true;
  }, [vaultToken, vaultExpires]);
  
  // Unlock vault with password
  const unlockVault = useCallback(async (password) => {
    try {
      const res = await axios.post(
        `${API}/vault-security/unlock`,
        { password },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.data.success) {
        const { vault_token, expires_at } = res.data.data;
        
        // Store in sessionStorage (not localStorage for security)
        sessionStorage.setItem(VAULT_TOKEN_KEY, vault_token);
        sessionStorage.setItem(VAULT_EXPIRES_KEY, expires_at);
        
        setVaultToken(vault_token);
        setVaultExpires(new Date(expires_at));
        setIsVaultLocked(false);
        setShowUnlockModal(false);
        
        // Reset activity timer
        lastActivityRef.current = Date.now();
        
        return { success: true };
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Kunne ikke låse opp Vault';
      return { success: false, error: message };
    }
  }, [token]);
  
  // Lock vault (manual or timeout)
  const lockVault = useCallback((reason = 'manual') => {
    sessionStorage.removeItem(VAULT_TOKEN_KEY);
    sessionStorage.removeItem(VAULT_EXPIRES_KEY);
    setVaultToken(null);
    setVaultExpires(null);
    setIsVaultLocked(true);
    setRemainingTime(0);
    
    // Log the lock event
    console.log(`Vault locked: ${reason}`);
  }, []);
  
  // Extend vault session (on activity)
  const extendSession = useCallback(async () => {
    if (!vaultToken || isVaultLocked) return;
    
    try {
      const res = await axios.post(
        `${API}/vault-security/extend`,
        {},
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'X-Vault-Token': vaultToken
          } 
        }
      );
      
      if (res.data.success) {
        const { vault_token, expires_at } = res.data.data;
        
        sessionStorage.setItem(VAULT_TOKEN_KEY, vault_token);
        sessionStorage.setItem(VAULT_EXPIRES_KEY, expires_at);
        
        setVaultToken(vault_token);
        setVaultExpires(new Date(expires_at));
      }
    } catch (error) {
      // Session could not be extended - it may have expired
      if (error.response?.status === 401) {
        lockVault('session_expired');
      }
    }
  }, [token, vaultToken, isVaultLocked, lockVault]);
  
  // Get signed download URL
  const getSignedDownloadUrl = useCallback(async (documentId) => {
    if (isVaultLocked || !vaultToken) {
      return { success: false, error: 'Vault er låst' };
    }
    
    try {
      const res = await axios.post(
        `${API}/vault-security/sign-download/${documentId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'X-Vault-Token': vaultToken
          }
        }
      );
      
      if (res.data.success) {
        return { 
          success: true, 
          data: res.data.data 
        };
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Kunne ikke opprette nedlastingslenke';
      
      // If vault session expired, lock it
      if (error.response?.status === 401) {
        lockVault('session_expired');
      }
      
      return { success: false, error: message };
    }
  }, [token, vaultToken, isVaultLocked, lockVault]);
  
  // Handle user activity
  const handleActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);
  
  // Check for inactivity
  const checkInactivity = useCallback(() => {
    const now = Date.now();
    const timeSinceActivity = now - lastActivityRef.current;
    
    if (timeSinceActivity >= INACTIVITY_TIMEOUT_MS && !isVaultLocked) {
      lockVault('inactivity');
    }
  }, [isVaultLocked, lockVault]);
  
  // Setup activity listeners
  useEffect(() => {
    ACTIVITY_EVENTS.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });
    
    return () => {
      ACTIVITY_EVENTS.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [handleActivity]);
  
  // Setup inactivity checker
  useEffect(() => {
    // Check every minute for inactivity
    inactivityTimerRef.current = setInterval(checkInactivity, 60 * 1000);
    
    return () => {
      if (inactivityTimerRef.current) {
        clearInterval(inactivityTimerRef.current);
      }
    };
  }, [checkInactivity]);
  
  // Setup countdown timer
  useEffect(() => {
    countdownTimerRef.current = setInterval(() => {
      checkVaultSession();
    }, 1000);
    
    return () => {
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
      }
    };
  }, [checkVaultSession]);
  
  // Initial check on mount
  useEffect(() => {
    checkVaultSession();
  }, [checkVaultSession]);
  
  // Clear vault session on logout
  useEffect(() => {
    if (!user) {
      lockVault('logout');
    }
  }, [user, lockVault]);
  
  // Get vault headers for API requests
  const getVaultHeaders = useCallback(() => {
    if (!vaultToken) return {};
    return { 'X-Vault-Token': vaultToken };
  }, [vaultToken]);
  
  // Format remaining time as MM:SS
  const formatRemainingTime = useCallback(() => {
    if (remainingTime <= 0) return '00:00';
    const minutes = Math.floor(remainingTime / 60);
    const seconds = remainingTime % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }, [remainingTime]);
  
  const value = {
    // State
    isVaultLocked,
    remainingTime,
    remainingTimeFormatted: formatRemainingTime(),
    showUnlockModal,
    
    // Actions
    unlockVault,
    lockVault,
    extendSession,
    getSignedDownloadUrl,
    setShowUnlockModal,
    
    // Helpers
    getVaultHeaders,
    vaultToken
  };
  
  return (
    <VaultSecurityContext.Provider value={value}>
      {children}
    </VaultSecurityContext.Provider>
  );
}

export function useVaultSecurity() {
  const context = useContext(VaultSecurityContext);
  if (!context) {
    throw new Error('useVaultSecurity must be used within a VaultSecurityProvider');
  }
  return context;
}

export default VaultSecurityContext;
